import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import { MoneyField, NumberField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { createShippingSupply } from "@/context/catalogSync";
import {
  generateId,
  STARTER_SHIPPING_SUPPLIES_CATALOG,
  useApp,
  type ShippingSupply,
  type SuppliesTrackingMode,
  type SupplyConsumption,
} from "@/context/AppContext";

interface CatalogEntry {
  id: string;
  name: string;
  /** Existing supply (server-known). undefined for starter items not yet
   *  inserted for this store. */
  existing?: ShippingSupply;
}

interface BatchDraft {
  /** PATCHed onto the supply on save. */
  unitsPerUse: string;
  /** Whether unitsPerUse multiplies per listing (default) or per card. */
  consumption: SupplyConsumption;
  /** cost-only: sets the supply's flat unitCost. full: sets unitCost via the
   *  first restock batch instead. */
  costPerUse: string;
  /** full mode only — restock batch the wizard seeds on save. */
  totalCost: string;
  unitsBought: string;
  supplier: string;
}

const EMPTY_BATCH: BatchDraft = {
  unitsPerUse: "1",
  consumption: "per-listing",
  costPerUse: "",
  totalCost: "",
  unitsBought: "",
  supplier: "",
};

/**
 * Resolve a wizard batch draft into a restock payload. Units-only entries are
 * accepted (free batch, cost defaults to 0) so the user can seed their initial
 * stock without having to type a price too. Returns null when nothing was
 * entered (we skip restock in that case).
 */
function parseBatch(
  draft: BatchDraft,
): { addedQty: number; totalCost: number; supplier?: string } | null {
  const addedQty = parseInt(draft.unitsBought, 10);
  if (isNaN(addedQty) || addedQty <= 0) return null;
  const totalCostRaw = parseFloat(draft.totalCost);
  const totalCost =
    !isNaN(totalCostRaw) && totalCostRaw >= 0 ? totalCostRaw : 0;
  return {
    addedQty,
    totalCost,
    supplier: draft.supplier.trim() || undefined,
  };
}

const MODE_LABEL: Record<SuppliesTrackingMode, string> = {
  off: "Don't track",
  "cost-only": "Track cost per use",
  full: "Track full history",
};

const MODE_HELP: Record<SuppliesTrackingMode, string> = {
  off: "Just record which supplies you carry. No cost math, no history.",
  "cost-only":
    "Set a flat cost-per-use for each supply. Edit it when prices change.",
  full: "Track every restock batch with supplier + weighted-average cost so you have a real audit trail.",
};

/**
 * Initialization wizard for /tools/supplies. Walks the operator through
 * (1) which supplies they carry, (2) how detailed they want bookkeeping,
 * (3) per-supply details for the chosen mode. Save composes the requisite
 * supply inserts/PATCHes + restock seeds + store-settings write.
 */
export function SuppliesWizard({
  initialMode,
  onClose,
}: {
  initialMode: SuppliesTrackingMode;
  onClose: () => void;
}) {
  const {
    shippingSupplies,
    addShippingSupply,
    updateShippingSupply,
    restockSupply,
    updateAppPreferences,
  } = useApp();

  // Build the catalog the wizard offers: every existing supply + every
  // starter item not yet inserted for this store (deduped by name).
  const catalog = useMemo<CatalogEntry[]>(() => {
    const existingByName = new Map(
      shippingSupplies.map((s) => [s.name.trim().toLowerCase(), s]),
    );
    const entries: CatalogEntry[] = shippingSupplies.map((s) => ({
      id: s.id,
      name: s.name,
      existing: s,
    }));
    for (const seed of STARTER_SHIPPING_SUPPLIES_CATALOG) {
      if (!existingByName.has(seed.name.trim().toLowerCase())) {
        entries.push({ id: seed.id, name: seed.name });
      }
    }
    return entries;
  }, [shippingSupplies]);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(catalog.filter((c) => c.existing?.enabled).map((c) => c.id)),
  );
  const [mode, setMode] = useState<SuppliesTrackingMode>(initialMode);
  const [drafts, setDrafts] = useState<Record<string, BatchDraft>>(() => {
    const out: Record<string, BatchDraft> = {};
    for (const c of catalog) {
      out[c.id] = {
        ...EMPTY_BATCH,
        unitsPerUse: String(c.existing?.unitsPerUse ?? 1),
        consumption: c.existing?.consumption ?? "per-listing",
        costPerUse:
          c.existing?.unitCost != null && c.existing.unitCost > 0
            ? c.existing.unitCost.toFixed(2)
            : "",
      };
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enabledEntries = catalog.filter((c) => selected.has(c.id));

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setDraft = (id: string, patch: Partial<BatchDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? EMPTY_BATCH), ...patch },
    }));
  };

  const goNext = () => setStep((s) => (s === 3 ? 3 : ((s + 1) as 1 | 2 | 3)));
  const goBack = () => setStep((s) => (s === 1 ? 1 : ((s - 1) as 1 | 2 | 3)));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // 1. For each catalog entry, settle its existence + enabled flag +
      // unitsPerUse + (cost-only) unitCost. Newly enabled starter items are
      // INSERTed; everything else is PATCHed.
      const inserts: ShippingSupply[] = [];
      const patches: ShippingSupply[] = [];
      const fullSeeds: Array<{
        supplyId: string;
        addedQty: number;
        totalCost: number;
        supplier?: string;
      }> = [];

      for (const entry of catalog) {
        const isSelected = selected.has(entry.id);
        const draft = drafts[entry.id] ?? EMPTY_BATCH;
        const unitsPerUse = Math.max(1, parseInt(draft.unitsPerUse, 10) || 1);

        if (entry.existing) {
          const next: ShippingSupply = {
            ...entry.existing,
            enabled: isSelected,
            unitsPerUse: isSelected ? unitsPerUse : entry.existing.unitsPerUse,
            consumption: isSelected
              ? draft.consumption
              : entry.existing.consumption,
          };
          if (isSelected && mode === "cost-only") {
            const v = parseFloat(draft.costPerUse);
            if (!isNaN(v) && v >= 0) next.unitCost = v;
          }
          patches.push(next);
        } else if (isSelected) {
          const fresh: ShippingSupply = {
            id: generateId("SUP"),
            name: entry.name,
            unitCost:
              mode === "cost-only"
                ? Math.max(0, parseFloat(draft.costPerUse) || 0)
                : 0,
            quantity: 0,
            enabled: true,
            unitsPerUse,
            consumption: draft.consumption,
            createdAt: new Date().toISOString(),
          };
          inserts.push(fresh);
          if (mode === "full") {
            const seed = parseBatch(draft);
            if (seed) fullSeeds.push({ supplyId: fresh.id, ...seed });
          }
        }
      }

      // Existing supplies that are still enabled in `full` mode can optionally
      // get a fresh batch too (if the user filled in the inputs).
      for (const entry of catalog) {
        if (!entry.existing || !selected.has(entry.id) || mode !== "full")
          continue;
        const draft = drafts[entry.id] ?? EMPTY_BATCH;
        const seed = parseBatch(draft);
        if (seed) fullSeeds.push({ supplyId: entry.existing.id, ...seed });
      }

      // 2a. AWAIT POST for new supplies so their restock POST below can
      // find them on the server. Then add to local state (skipSync to
      // avoid double-POST).
      await Promise.all(inserts.map((fresh) => createShippingSupply(fresh)));
      for (const fresh of inserts) addShippingSupply(fresh, { skipSync: true });
      // 2b. Existing supplies: fire-and-forget PATCH (no follow-up POST
      // depends on them landing first).
      for (const next of patches) updateShippingSupply(next);

      // 3. Seed restock events for full-mode entries with valid batch data.
      // Safe to fan out now that any inserts above completed server-side.
      if (fullSeeds.length > 0) {
        await Promise.all(
          fullSeeds.map((b) =>
            restockSupply(b.supplyId, {
              addedQty: b.addedQty,
              totalCost: b.totalCost,
              supplier: b.supplier,
            }),
          ),
        );
      }

      // 4. Persist the tracking mode + initialized flag. Awaited so an
      // immediate page reload after Save sees the new prefs reliably.
      await updateAppPreferences({
        supplies: { trackingMode: mode, initialized: true },
      });

      onClose();
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : "Could not save — please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-background/80 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex w-full max-w-2xl flex-col rounded-md border border-border bg-card shadow-lg sm:max-h-[85vh]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Step {step} of 3
            </div>
            <div className="text-base font-semibold text-foreground">
              {step === 1
                ? "Pick the supplies you carry"
                : step === 2
                  ? "How do you want to track them?"
                  : MODE_LABEL[mode]}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {step === 1 && (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {catalog.map((entry) => {
                const checked = selected.has(entry.id);
                return (
                  <li key={entry.id}>
                    <button
                      type="button"
                      onClick={() => toggleSelected(entry.id)}
                      className={`flex w-full items-center gap-2 rounded-md border p-2 text-left ${
                        checked
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card hover:bg-muted"
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border ${
                          checked
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border"
                        }`}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="flex-1 truncate text-sm text-foreground">
                        {entry.name}
                      </span>
                      {entry.existing && (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          existing
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {step === 2 && (
            <ul className="space-y-2">
              {(["off", "cost-only", "full"] as const).map((m) => {
                const active = mode === m;
                return (
                  <li key={m}>
                    <button
                      type="button"
                      onClick={() => setMode(m)}
                      className={`flex w-full flex-col gap-1 rounded-md border p-3 text-left ${
                        active
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card hover:bg-muted"
                      }`}
                    >
                      <span className="text-sm font-semibold text-foreground">
                        {MODE_LABEL[m]}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {MODE_HELP[m]}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {mode === "off" && (
                <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                  Nothing else to enter — we'll just record which supplies you
                  carry. You can switch tracking modes any time from the
                  supplies page.
                </p>
              )}
              {enabledEntries.length === 0 && mode !== "off" && (
                <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                  No supplies selected — go back to step 1.
                </p>
              )}
              {mode === "cost-only" &&
                enabledEntries.map((entry) => {
                  const draft = drafts[entry.id] ?? EMPTY_BATCH;
                  return (
                    <div
                      key={entry.id}
                      className="rounded-md border border-border bg-card p-3"
                    >
                      <div className="mb-1 text-sm font-semibold text-foreground">
                        {entry.name}
                      </div>
                      <MoneyField
                        label="Cost per use"
                        value={draft.costPerUse}
                        onChangeText={(v) =>
                          setDraft(entry.id, { costPerUse: v })
                        }
                      />
                    </div>
                  );
                })}
              {mode === "full" && enabledEntries.length > 0 && (
                <p className="rounded-md bg-muted px-3 py-2 text-[11px] text-muted-foreground">
                  Enter <strong>Quantity on hand</strong> to seed your starting
                  stock; add <strong>Price paid</strong> +{" "}
                  <strong>Supplier</strong>
                  to record where the batch came from. Leave all batch fields
                  blank to just enable the supply with no opening stock.
                </p>
              )}
              {mode === "full" &&
                enabledEntries.map((entry) => {
                  const draft = drafts[entry.id] ?? EMPTY_BATCH;
                  return (
                    <div
                      key={entry.id}
                      className="rounded-md border border-border bg-card p-3"
                    >
                      <div className="mb-1 text-sm font-semibold text-foreground">
                        {entry.name}
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <NumberField
                          label="Quantity on hand"
                          value={draft.unitsBought}
                          onChangeText={(v) =>
                            setDraft(entry.id, { unitsBought: v })
                          }
                          placeholder="0"
                        />
                        <MoneyField
                          label="Price paid (optional)"
                          value={draft.totalCost}
                          onChangeText={(v) =>
                            setDraft(entry.id, { totalCost: v })
                          }
                        />
                        <div className="mb-4 flex flex-col gap-1.5">
                          <label className="text-xs font-medium tracking-wide text-muted-foreground">
                            Units consumed
                          </label>
                          <div className="flex items-stretch gap-1.5">
                            <div className="flex items-center rounded-md border border-border bg-card px-3 focus-within:border-primary">
                              <input
                                value={draft.unitsPerUse}
                                onChange={(e) =>
                                  setDraft(entry.id, {
                                    unitsPerUse: e.target.value.replace(
                                      /\D/g,
                                      "",
                                    ),
                                  })
                                }
                                inputMode="numeric"
                                placeholder="1"
                                className="min-h-[44px] w-16 bg-transparent py-3 text-center text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                              />
                            </div>
                            <select
                              value={draft.consumption}
                              onChange={(e) =>
                                setDraft(entry.id, {
                                  consumption: e.target
                                    .value as SupplyConsumption,
                                })
                              }
                              className="min-h-[44px] flex-1 rounded-md border border-border bg-card px-3 text-[15px] text-foreground focus:border-primary focus:outline-none"
                            >
                              <option
                                value="per-listing"
                                className="bg-card text-foreground"
                              >
                                per listing (typical for mailers)
                              </option>
                              <option
                                value="per-card"
                                className="bg-card text-foreground"
                              >
                                per card (e.g. penny sleeves)
                              </option>
                            </select>
                          </div>
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5">
                          <label className="text-xs font-medium tracking-wide text-muted-foreground">
                            Supplier{" "}
                            <span className="text-muted-foreground">
                              (optional)
                            </span>
                          </label>
                          <div className="flex items-center rounded-md border border-border bg-card px-3 focus-within:border-primary">
                            <input
                              value={draft.supplier}
                              onChange={(e) =>
                                setDraft(entry.id, {
                                  supplier: e.target.value,
                                })
                              }
                              placeholder="e.g. Jays Sports"
                              className="min-h-[44px] w-full bg-transparent py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {saveError && (
          <div className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-destructive">
            {saveError}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={step === 1 ? onClose : goBack}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/70"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 3 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={step === 1 && selected.size === 0}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
            >
              Next
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <PrimaryButton
              label={saving ? "Saving…" : "Save & finish"}
              onClick={handleSave}
              loading={saving}
              leadingIcon={
                saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
